import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  checkConnectionStatus,
  getAuthorizeUrl,
  disconnectCashCalc,
  syncToCashCalc,
  validateCashCalcClient,
  clearCashCalcState,
  resetSyncState,
} from "../../../redux/cashcalcSlice";
import { getCashCalcCredentialsList } from "../../../redux/SystemManager/cashcalcCredentialsSlice";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import PopupTable from "../../DynamicReport/components/PopupTable";
import { fetchApiData } from "../../DynamicReport/serviceFile";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Select,
  Spinner,
  Toaster,
  tokens,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Filled,
  DismissCircle20Filled,
  ErrorCircle20Filled,
  Info12Regular,
  Print20Regular,
  Warning20Filled,
} from "@fluentui/react-icons";

// ─── Scope metadata ───────────────────────────────────────────────────────────

const SCOPE_LABELS: Record<string, string> = {
  "client-info":     "Client - Info",
  "client-finances": "Client - Finances",
};

const SCOPE_SECTIONS: Record<string, { key: string; label: string }[]> = {
  "client-info": [
    { key: "personals",  label: "Personal Information" },
    { key: "contacts",   label: "Contact Methods" },
    { key: "addresses",  label: "Addresses" },
    { key: "health",     label: "Health" },
    { key: "notes",      label: "Notes" },
    { key: "objectives", label: "Objectives" },
    { key: "employment", label: "Employment" },
    { key: "dependants", label: "Relations / Dependants" },
  ],
  "client-finances": [
    { key: "liabilities",  label: "Liabilities" },
    { key: "pensions",     label: "Pensions" },
    { key: "investments",  label: "Investments" },
    { key: "assets",       label: "Assets" },
    { key: "policies",     label: "Policies" },
    { key: "incomes",      label: "Incomes" },
    { key: "expenses",     label: "Expenses" },
  ],
};

// ─── Status helpers ───────────────────────────────────────────────────────────

function statusColors(status: string): { bg: string; border: string; text: string } {
  switch (status) {
    case "success":
      return { bg: tokens.colorPaletteGreenBackground1, border: tokens.colorPaletteGreenBorderActive, text: tokens.colorPaletteGreenForeground1 };
    case "partial_success":
    case "partial_delete_warning":
      return { bg: tokens.colorPaletteMarigoldBackground1, border: tokens.colorPaletteMarigoldBorderActive, text: tokens.colorPaletteMarigoldForeground1 };
    case "delete_skipped":
      return { bg: tokens.colorNeutralBackground3, border: tokens.colorNeutralStroke2, text: tokens.colorNeutralForeground3 };
    default:
      return { bg: tokens.colorPaletteRedBackground1, border: tokens.colorPaletteRedBorderActive, text: tokens.colorPaletteRedForeground1 };
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "success":                return "Success";
    case "partial_success":        return "Partial Success";
    case "partial_delete_warning": return "Partial (Delete Warning)";
    case "delete_skipped":         return "Skipped";
    default:                       return status || "Failed";
  }
}

function SectionStatusIcon({ status }: { status?: string }) {
  if (!status) return <Spinner size="tiny" />;
  if (status === "success")
    return <CheckmarkCircle20Filled style={{ color: tokens.colorPaletteGreenForeground1, flexShrink: 0 }} />;
  if (status === "partial_success" || status === "partial_delete_warning")
    return <Warning20Filled style={{ color: tokens.colorPaletteMarigoldForeground1, flexShrink: 0 }} />;
  if (status === "delete_skipped")
    return <DismissCircle20Filled style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />;
  return <DismissCircle20Filled style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }} />;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: { padding: "24px", maxWidth: "800px" },
  title: { fontSize: "20px", fontWeight: "600", marginBottom: "20px" },
  section: {
    marginBottom: "24px",
    padding: "16px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "12px",
    color: tokens.colorNeutralForeground1,
  },
  connectionBar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  connectedBar:    { backgroundColor: tokens.colorPaletteGreenBackground1, border: `1px solid ${tokens.colorPaletteGreenBorderActive}` },
  disconnectedBar: { backgroundColor: tokens.colorPaletteMarigoldBackground1, border: `1px solid ${tokens.colorPaletteMarigoldBorderActive}` },
  connectionText:  { flex: 1, fontWeight: "500" },
  scopeGroup:      { display: "flex", flexDirection: "column", gap: "8px" },
  syncActions:     { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" },

  // Sync results
  resultRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "6px",
    marginBottom: "8px",
    fontSize: "13px",
  },
  resultLabel: { flex: 1, fontWeight: "500" },

  // Validate Data button — colours inverted relative to the primary "Export to
  // CashCalc" button (asc-button-primary/var(--button-color)) for contrast.
  // Explicit :disabled styling is required because the hardcoded base colours
  // would otherwise override Fluent's normal disabled dimming.
  invertedBrandBtn: {
    backgroundColor: "#fff",
    color: "var(--button-color)",
    border: "1px solid var(--button-color)",
    ":hover": { backgroundColor: "#f0f6ff", color: "var(--button-color)", border: "1px solid var(--button-color)" },
    ":hover:active": { backgroundColor: "#e0edff", color: "var(--button-color)" },
    ":disabled": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      border: `1px solid ${tokens.colorNeutralStrokeDisabled}`,
    },
    ":disabled:hover": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      border: `1px solid ${tokens.colorNeutralStrokeDisabled}`,
    },
  },

  // Progress dialog sections
  progressScopeHeading: {
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    marginTop: "16px",
    marginBottom: "8px",
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "5px 0",
    fontSize: "13px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  progressRowLabel: { flex: 1 },
  progressStatus: { fontSize: "12px", fontWeight: "500" },

  // Validation report
  reportSummary: {
    display: "flex",
    gap: "16px",
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground2,
    marginBottom: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  reportSummaryItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "500",
  },
  reportSection: {
    marginBottom: "16px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "6px",
    overflow: "hidden",
  },
  reportSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: "13px",
    fontWeight: "600",
  },
  reportSectionNames: { display: "flex", alignItems: "center", gap: "6px", flex: 1 },
  reportSectionArrow: { color: tokens.colorNeutralForeground3, fontSize: "11px", margin: "0 2px" },
  reportIssueList:    { listStyle: "none", margin: 0, padding: 0 },
  reportIssue: {
    display: "grid",
    gridTemplateColumns: "20px auto",
    gap: "8px",
    padding: "8px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: "12px",
    alignItems: "start",
  },
  reportIssueError:   { backgroundColor: "#fff5f5" },
  reportIssueWarning: { backgroundColor: "#fffbf0" },
  issueRecord: { fontWeight: "600", color: tokens.colorNeutralForeground1 },
  issueField:  { color: tokens.colorBrandForeground1, fontStyle: "italic", marginRight: "4px" },
  issueText:   { color: tokens.colorNeutralForeground2 },
  noIssues:    { padding: "8px 12px", fontSize: "12px", color: tokens.colorNeutralForeground3 },
});

// ─── Print helper ─────────────────────────────────────────────────────────────

function esc(s: any): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildPrintHtml(report: any): string {
  const date = report.generatedAt
    ? new Date(report.generatedAt).toLocaleString()
    : new Date().toLocaleString();

  const sectionHtml = report.sections
    .map((s: any) => {
      const issues = s.issues || [];
      const issueHtml =
        issues.length === 0
          ? '<p style="color:#666;font-size:12px;margin:0;padding:6px 0">No issues found</p>'
          : issues
              .map(
                (iss: any) => `
          <div style="display:grid;grid-template-columns:16px auto;gap:6px;padding:6px 0;border-bottom:1px solid #eee;font-size:12px;align-items:start">
            <span style="color:${iss.severity === "error" ? "#c62828" : "#e65100"};font-weight:bold;font-size:14px">${iss.severity === "error" ? "✕" : "⚠"}</span>
            <div>
              <span style="font-weight:600">${esc(iss.record)}</span> &nbsp;·&nbsp;
              <span style="color:#1a73e8;font-style:italic">${esc(iss.field)}</span><br/>
              <span style="color:#444">${esc(iss.issue)}</span>
            </div>
          </div>`
              )
              .join("");

      return `
      <div style="margin-bottom:16px;border:1px solid #ddd;border-radius:6px;overflow:hidden">
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 12px;background:#f5f5f5;border-bottom:1px solid #ddd">
          <strong style="font-size:13px">${esc(s.waName)}</strong>
          <span style="font-size:11px;color:#666">→ CashCalc: ${esc(s.ccName)} &nbsp;|&nbsp; ${s.totalChecked} checked &nbsp;|&nbsp; ${issues.length} issue${issues.length !== 1 ? "s" : ""}</span>
        </div>
        <div style="padding:8px 12px">${issueHtml}</div>
      </div>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html><head><title>CashCalc Validation Report — ${esc(report.clientName)}</title>
<style>
  body{font-family:Arial,sans-serif;margin:24px;color:#222;font-size:13px}
  h1{font-size:18px;margin-bottom:4px}
  .meta{color:#666;font-size:12px;margin-bottom:16px}
  .summary{display:flex;gap:24px;padding:10px 14px;background:#f0f4ff;border-radius:6px;margin-bottom:20px;font-size:13px;font-weight:500}
  .err{color:#c62828} .warn{color:#e65100} .ok{color:#2e7d32}
  @media print{a{display:none}}
</style></head>
<body>
  <h1>CashCalc Validation Report</h1>
  <div class="meta">Client: <strong>${esc(report.clientName)}</strong>&nbsp;&nbsp;·&nbsp;&nbsp;Generated: ${esc(date)}&nbsp;&nbsp;·&nbsp;&nbsp;Scopes: ${(report.scopes || []).join(", ")}</div>
  <div class="summary">
    <span>Checked: <strong>${report.summary?.totalChecked ?? 0}</strong> records</span>
    <span class="err">Errors: <strong>${report.summary?.errors ?? 0}</strong></span>
    <span class="warn">Warnings: <strong>${report.summary?.warnings ?? 0}</strong></span>
  </div>
  ${sectionHtml}
</body></html>`;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CashCalcPage: React.FC = () => {
  const styles    = useStyles();
  const dispatch  = useDispatch<AppDispatch>();
  const { showToast: notify, toasterId } = useToastMessage();

  const { connected, adviserName, connectionLoading, syncLoading, syncResults, validateLoading, validationReport } =
    useSelector((state: RootState) => state.cashcalc);
  const { listItems: credentialsList } = useSelector((state: RootState) => state.cashcalcCredentials);

  const [selectedConsultantId, setSelectedConsultantId] = useState("");
  const [scopes, setScopes] = useState({ "client-info": false, "client-finances": false });
  const [selectedWaCustomerId, setSelectedWaCustomerId] = useState("");
  const [selectedWaClientName, setSelectedWaClientName] = useState("");

  // WA client picker
  const CLIENT_PARAM: any = {
    type: "uuid",
    label: "Client List",
    popup: true,
    category: "select",
    required: true,
    reportParam: "customerId",
    apiEndpoint: {
      value: "customer_id",
      apiUrl: "<<BASE_URL>>/client",
      format: "list",
      responseData: "data.customerList",
      attributes: [
        { value: "first_names", label: "First Name" },
        { value: "last_name",   label: "Last Name" },
      ],
      count: "data.count",
      pagination: true,
      QueryParams: ["type", "limit", "page", "search"],
    },
    multiSelect: false,
    placeholder: "Select a Client",
  };

  const [clientPopup, setClientPopup] = useState<any>({
    data: [],
    selected: null,
    total: 0,
    isOpen: false,
    pagination: { pageIndex: 0, pageSize: 10 },
    searchQuery: "",
    rowSelection: {},
  });

  // Validation report dialog
  const [reportOpen, setReportOpen] = useState(false);

  // Sync progress dialog
  const [progressOpen,  setProgressOpen]  = useState(false);
  const [progressDone,  setProgressDone]  = useState(false);
  const [progressScopes, setProgressScopes] = useState<string[]>([]);
  // Maps 'scope:sectionKey' → section result data once revealed
  const [revealedData, setRevealedData] = useState<Record<string, any>>({});
  const syncTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Detail dialog (view per-section breakdown of a sync result)
  const [detailOpen,  setDetailOpen]  = useState(false);
  const [detailScope, setDetailScope] = useState<any>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // The popup-based OAuth flow's completion is detected via handleConnect's
    // connection-status poll (pollIntervalRef), not here — CashCalcCallback.tsx
    // (the popup's own landing page) exchanges the code itself directly and never
    // hands anything back to this tab via sessionStorage or otherwise.
    dispatch(checkConnectionStatus()).catch(() => {});
    dispatch(getCashCalcCredentialsList()).catch(() => {});
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [dispatch]);

  // ─── OAuth ────────────────────────────────────────────────────────────────

  const [preloadedAuthorizeUrl, setPreloadedAuthorizeUrl] = useState("");
  const [authorizeUrlLoading, setAuthorizeUrlLoading] = useState(false);
  const [authorizeUrlError, setAuthorizeUrlError] = useState(false);
  const authorizeUrlRequestIdRef = useRef(0);

  // Fetches the OAuth authorize URL ahead of time (as soon as a consultant is
  // selected) so handleConnect's window.open() call can run fully synchronously
  // within the click gesture, with noopener/noreferrer intact. Safari/WebKit (and
  // other browsers' popup blockers) only treat window.open as trusted when it's
  // called synchronously in the gesture that triggered it — an intervening await
  // spends that trust and the popup gets silently blocked. noopener/noreferrer
  // (kept here deliberately, since this opens a third-party OAuth page — dropping
  // it would reopen a reverse-tabnabbing hole) makes window.open always return
  // null regardless of outcome, so unlike handlePrint's popup this one can't be
  // null-checked for "blocked" — pre-fetching is the only way to fix the
  // synchronous-gesture problem without giving that protection up.
  const preloadAuthorizeUrl = (consultantUserId: string) => {
    const requestId = ++authorizeUrlRequestIdRef.current;
    setPreloadedAuthorizeUrl("");
    setAuthorizeUrlError(false);
    setAuthorizeUrlLoading(true);
    dispatch(getAuthorizeUrl(consultantUserId)).then((result: any) => {
      if (authorizeUrlRequestIdRef.current !== requestId) return; // superseded by a newer selection
      setAuthorizeUrlLoading(false);
      if (getAuthorizeUrl.fulfilled.match(result)) {
        setPreloadedAuthorizeUrl(result.payload);
      } else {
        setAuthorizeUrlError(true);
      }
    });
  };

  useEffect(() => {
    if (!selectedConsultantId) {
      authorizeUrlRequestIdRef.current++; // invalidate any in-flight request
      setPreloadedAuthorizeUrl("");
      setAuthorizeUrlError(false);
      setAuthorizeUrlLoading(false);
      return;
    }
    preloadAuthorizeUrl(selectedConsultantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConsultantId]);

  const handleConnect = () => {
    if (!selectedConsultantId) {
      notify("Select Account", "Please select a CashCalc account first.", "warning");
      return;
    }
    if (authorizeUrlError) {
      notify("Retrying", "Preparing the CashCalc connection again — try Connect once more in a moment.", "warning");
      preloadAuthorizeUrl(selectedConsultantId);
      return;
    }
    if (!preloadedAuthorizeUrl) {
      notify("Please wait", "Still preparing the CashCalc connection — try again in a moment.", "warning");
      return;
    }
    window.open(preloadedAuthorizeUrl, "_blank", "width=600,height=700,noopener,noreferrer");
    let notified = false;
    pollIntervalRef.current = setInterval(async () => {
      if (notified) return;
      const statusResult = await dispatch(checkConnectionStatus());
      if (checkConnectionStatus.fulfilled.match(statusResult) && statusResult.payload?.connected && !notified) {
        notified = true;
        clearInterval(pollIntervalRef.current!);
        pollIntervalRef.current = null;
        notify("Connected", "Successfully connected to CashCalc!");
      }
    }, 2000);
    setTimeout(() => {
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    }, 120000);
  };

  const handleDisconnect = async () => {
    await dispatch(disconnectCashCalc());
    dispatch(clearCashCalcState());
    notify("Disconnected", "CashCalc session cleared.");
  };

  // ─── WA client picker ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!clientPopup.isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchApiData(CLIENT_PARAM.apiEndpoint.apiUrl, {
          type: "client_list",
          limit: clientPopup.pagination.pageSize,
          page: clientPopup.pagination.pageIndex + 1,
          search: clientPopup.searchQuery || "",
        });
        const data  = response?.data?.customerList || [];
        const total = response?.data?.count || 0;
        if (!cancelled) setClientPopup((prev: any) => ({ ...prev, data, total }));
      } catch {
        if (!cancelled) setClientPopup((prev: any) => ({ ...prev, data: [], total: 0 }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPopup.isOpen, clientPopup.pagination.pageIndex, clientPopup.pagination.pageSize, clientPopup.searchQuery]);

  const openWaPicker = () =>
    setClientPopup((prev: any) => ({
      ...prev,
      isOpen: true,
      pagination: { pageIndex: 0, pageSize: 10 },
      searchQuery: "",
      rowSelection: {},
      selected: null,
    }));

  const handleClientPopupClose   = () => setClientPopup((prev: any) => ({ ...prev, isOpen: false, rowSelection: {}, selected: null }));
  const handleClientRowSelection = (updater: any) =>
    setClientPopup((prev: any) => {
      const newRowSelection = typeof updater === "function" ? updater(prev.rowSelection) : updater;
      const idx  = Object.keys(newRowSelection).find((k) => newRowSelection[k]);
      const selected = idx !== undefined ? prev.data[parseInt(idx)] : null;
      return { ...prev, rowSelection: newRowSelection, selected };
    });
  const handleClientPagination  = (updater: any) =>
    setClientPopup((prev: any) => {
      const p = typeof updater === "function" ? updater(prev.pagination) : updater;
      return { ...prev, pagination: p, isOpen: true };
    });
  const handleClientGlobalFilter = (value: string) =>
    setClientPopup((prev: any) => ({ ...prev, searchQuery: value || "", pagination: { ...prev.pagination, pageIndex: 0 } }));
  const handleClientSelect = (selected: any) => {
    if (selected?.customer_id) {
      setSelectedWaCustomerId(selected.customer_id);
      setSelectedWaClientName(`${selected.first_names || ""} ${selected.last_name || ""}`.trim() || selected.customer_id);
    }
  };

  // ─── Sync ─────────────────────────────────────────────────────────────────

  const clearSyncTimers = () => {
    syncTimersRef.current.forEach(clearTimeout);
    syncTimersRef.current = [];
  };

  // Clears any in-flight staggered-reveal timers if the component unmounts mid-
  // animation (e.g. navigating away from /utilities/cashcalc during a sync) —
  // otherwise they'd still fire and call setRevealedData/setProgressOpen on an
  // unmounted component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => clearSyncTimers(), []);

  const handleProgressClose = () => {
    clearSyncTimers();
    setProgressOpen(false);
  };

  const handleSync = async () => {
    if (!selectedWaCustomerId) {
      notify("Missing Data", "Please select a WA client first.", "warning");
      return;
    }
    const selectedScopes = Object.entries(scopes)
      .filter(([, checked]) => checked)
      .map(([scope]) => scope);

    if (selectedScopes.length === 0) {
      notify("Missing Data", "Please select at least one data scope.", "warning");
      return;
    }

    // Open progress dialog — all sections show as pending
    setProgressOpen(true);
    setProgressDone(false);
    setProgressScopes(selectedScopes);
    setRevealedData({});
    clearSyncTimers();

    const result = await dispatch(syncToCashCalc({ customerId: selectedWaCustomerId, scopes: selectedScopes }));

    if (syncToCashCalc.fulfilled.match(result)) {
      const allResults: any[] = result.payload?.results || [];
      // Only display client-info and client-finances (not partner-info)
      const displayResults = allResults.filter((r: any) => r.scope !== "partner-info");

      setProgressDone(true);

      // Build ordered list of all sections across displayed scopes
      const allSections: { key: string; data: any }[] = [];
      for (const scopeResult of displayResults) {
        for (const sec of SCOPE_SECTIONS[scopeResult.scope] || []) {
          allSections.push({
            key:  `${scopeResult.scope}:${sec.key}`,
            data: scopeResult.data?.[sec.key] ?? null,
          });
        }
      }

      // Two-phase reveal: each section first appears as loading (null), then shows data.
      // This gives sequential buffering — scope headers appear when their first section
      // becomes visible, and each section buffers briefly before showing its result.
      const STEP_MS   = 280; // gap between sections becoming visible
      const BUFFER_MS = 140; // how long each section shows as loading before data appears

      allSections.forEach(({ key, data }, i) => {
        // Phase 1: section becomes visible in loading state
        syncTimersRef.current.push(setTimeout(() => {
          setRevealedData((prev) => ({ ...prev, [key]: null }));
        }, i * STEP_MS));
        // Phase 2: section shows its actual result
        syncTimersRef.current.push(setTimeout(() => {
          setRevealedData((prev) => ({ ...prev, [key]: data ?? { status: "success" } }));
        }, i * STEP_MS + BUFFER_MS));
      });

      // Auto-close 3s after the last section fully reveals
      const lastRevealAt = allSections.length > 0
        ? (allSections.length - 1) * STEP_MS + BUFFER_MS
        : 0;
      syncTimersRef.current.push(setTimeout(() => setProgressOpen(false), lastRevealAt + 3000));

      // Toast
      const hasPartial = displayResults.some(
        (r: any) => r.status === "partial_success" || r.status === "partial_delete_warning"
      );
      if (hasPartial) {
        notify("Sync Partial Success", "Some data synced with warnings. See the results below.", "warning");
      } else {
        notify("Sync Complete", `Successfully synced ${selectedScopes.length} scope(s) to CashCalc.`);
      }
    } else {
      setProgressOpen(false);
      const errMsg = (result as any).payload || "Unknown error — check the cashcalc MS logs.";
      notify("Sync Failed", `${errMsg}`, "error");
    }
  };

  // ─── Validate ─────────────────────────────────────────────────────────────

  const handleValidateClick = async () => {
    if (!selectedWaCustomerId) {
      notify("Select Client", "Please select a WA client before validating.", "warning");
      return;
    }
    const selectedScopes = Object.entries(scopes)
      .filter(([, checked]) => checked)
      .map(([scope]) => scope);
    if (selectedScopes.length === 0) {
      notify("Missing Data", "Please select at least one data scope.", "warning");
      return;
    }
    const result = await dispatch(validateCashCalcClient({ customerId: selectedWaCustomerId, scopes: selectedScopes }));
    if (validateCashCalcClient.fulfilled.match(result)) {
      setReportOpen(true);
    } else {
      notify("Validation Failed", (result as any).payload || "Validation failed — check the cashcalc MS logs.", "error");
    }
  };

  const handlePrint = () => {
    if (!validationReport) return;
    const html = buildPrintHtml(validationReport);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { notify("Blocked", "Please allow popups to print the report.", "warning"); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  // ─── Reset ────────────────────────────────────────────────────────────────

  // Resets the whole screen — client selection, scopes, and sync/validate
  // results — to prep for a new sync/validate run. Does not disconnect.
  const handleResetSelection = () => {
    setSelectedWaCustomerId("");
    setSelectedWaClientName("");
    setScopes({ "client-info": false, "client-finances": false });
    dispatch(resetSyncState());
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  // Minimum requirements shared by Export and Validate: a client selected and
  // at least one data scope checked.
  const hasMinimumSyncRequirements = !!selectedWaCustomerId && Object.values(scopes).some(Boolean);

  const displayedResults = syncResults.filter((r: any) => r.scope !== "partner-info");

  const report         = validationReport;
  const reportSections = report?.sections || [];
  const totalErrors    = report?.summary?.errors       ?? 0;
  const totalWarnings  = report?.summary?.warnings     ?? 0;
  const totalChecked   = report?.summary?.totalChecked ?? 0;

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div className={styles.title}>Export to CashCalc</div>

      {/* ── Connection bar ── */}
      <div className={`${styles.connectionBar} ${connected ? styles.connectedBar : styles.disconnectedBar}`}>
        {connected
          ? <CheckmarkCircle20Filled color={tokens.colorPaletteGreenForeground1} />
          : <DismissCircle20Filled   color={tokens.colorPaletteMarigoldForeground1} />}
        <span className={styles.connectionText}>
          {connected
            ? (adviserName ? <>Connected to CashCalc Account : <strong>{adviserName}</strong></> : "Connected to CashCalc")
            : "Not connected to CashCalc"}
        </span>
        {connected && (
          <Button
            appearance="secondary"
            className="asc-button-secondary"
            style={{ color: "red" }}
            onClick={handleDisconnect}
          >
            Disconnect
          </Button>
        )}
      </div>

      {/* ── Connect panel ── */}
      {!connected && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Connect to CashCalc</div>
          {credentialsList.length === 0 ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>No CashCalc accounts available</MessageBarTitle>
                You don't have access to any consultant with saved CashCalc credentials. Contact your administrator if you believe this is incorrect.
              </MessageBarBody>
            </MessageBar>
          ) : (
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
              <Field label="CashCalc Account" style={{ flex: 1, minWidth: 0 }}>
                <Select
                  style={{ width: "100%" }}
                  value={selectedConsultantId}
                  onChange={(_, d) => setSelectedConsultantId(d.value)}
                >
                  <option value="">Select consultant…</option>
                  {credentialsList.map((c: any) => (
                    <option key={c.UserId} value={c.UserId}>
                      {c.FullName || c.UserEmail || c.UserId}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                appearance="primary"
                className="asc-button-primary"
                shape="rounded"
                size="medium"
                onClick={handleConnect}
                disabled={connectionLoading || !selectedConsultantId || authorizeUrlLoading}
                icon={connectionLoading || authorizeUrlLoading ? <Spinner size="tiny" /> : undefined}
              >
                {connectionLoading ? "Connecting…" : "Connect to CashCalc"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Connected: client selection + scopes + sync ── */}
      {connected && (
        <>
          {/* Client selection */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Select a Client</div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <Button appearance="outline" onClick={openWaPicker}>
                {selectedWaClientName || "Select Client"}
              </Button>
              {selectedWaClientName && (
                <Button
                  appearance="secondary"
                  className="asc-button-secondary"
                  style={{ color: "red" }}
                  onClick={() => { setSelectedWaCustomerId(""); setSelectedWaClientName(""); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Validation report — same dialog config as the View Details popup */}
          <Dialog open={reportOpen} onOpenChange={(_, d) => setReportOpen(d.open)}>
            <DialogSurface style={{ maxWidth: "600px", width: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
              <DialogBody style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <DialogTitle>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Validation Report — {report?.clientName}</span>
                    <Button
                      size="small"
                      appearance="outline"
                      icon={<Print20Regular />}
                      onClick={handlePrint}
                      style={{ marginRight: "32px" }}
                    >
                      Print / Save as PDF
                    </Button>
                  </div>
                </DialogTitle>
                <DialogContent style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                  {report && (
                    <>
                      <div className={styles.reportSummary}>
                        <span className={styles.reportSummaryItem}>
                          Checked: <strong>{totalChecked}</strong>
                        </span>
                        <span className={styles.reportSummaryItem} style={{ color: totalErrors > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground2 }}>
                          {totalErrors > 0 && <ErrorCircle20Filled style={{ color: tokens.colorPaletteRedForeground1 }} />}
                          <strong>{totalErrors}</strong> error{totalErrors !== 1 ? "s" : ""}
                        </span>
                        <span className={styles.reportSummaryItem} style={{ color: totalWarnings > 0 ? tokens.colorPaletteMarigoldForeground1 : tokens.colorNeutralForeground2 }}>
                          {totalWarnings > 0 && <Warning20Filled style={{ color: tokens.colorPaletteMarigoldForeground1 }} />}
                          <strong>{totalWarnings}</strong> warning{totalWarnings !== 1 ? "s" : ""}
                        </span>
                        {totalErrors === 0 && totalWarnings === 0 && (
                          <span className={styles.reportSummaryItem} style={{ color: tokens.colorPaletteGreenForeground1 }}>
                            <CheckmarkCircle20Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                            All clear — data looks good!
                          </span>
                        )}
                        {report.hasPartner && <Badge color="informative" size="small">Partner included</Badge>}
                      </div>

                      <p style={{ fontSize: "12px", color: tokens.colorNeutralForeground3, marginBottom: "8px" }}>
                        Each section heading below reads <strong>Wealth Assyst → CashCalc</strong> — the name on the left is where to go in Wealth Assyst to fix an issue; the name on the right is the matching CashCalc record.
                      </p>

                      {reportSections.map((s: any) => {
                        const issues    = s.issues || [];
                        const errCount  = issues.filter((i: any) => i.severity === "error").length;
                        const warnCount = issues.filter((i: any) => i.severity === "warning").length;
                        return (
                          <div key={s.id} className={styles.reportSection}>
                            <div className={styles.reportSectionHeader}>
                              <div className={styles.reportSectionNames}>
                                <span>{s.waName}</span>
                                <span className={styles.reportSectionArrow}>→</span>
                                <span style={{ color: tokens.colorNeutralForeground3, fontWeight: "normal" }}>{s.ccName}</span>
                              </div>
                              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <span style={{ fontSize: "11px", color: tokens.colorNeutralForeground3 }}>{s.totalChecked} checked</span>
                                {errCount  > 0 && <Badge color="danger"  size="small">{errCount} error{errCount !== 1 ? "s" : ""}</Badge>}
                                {warnCount > 0 && <Badge color="warning" size="small">{warnCount} warning{warnCount !== 1 ? "s" : ""}</Badge>}
                                {issues.length === 0 && <Badge color="success" size="small">OK</Badge>}
                              </div>
                            </div>
                            {issues.length === 0 ? (
                              <div className={styles.noIssues}>No issues found</div>
                            ) : (
                              <ul className={styles.reportIssueList}>
                                {issues.map((iss: any, idx: number) => (
                                  <li key={idx} className={`${styles.reportIssue} ${iss.severity === "error" ? styles.reportIssueError : styles.reportIssueWarning}`}>
                                    <span style={{ paddingTop: "1px" }}>
                                      {iss.severity === "error"
                                        ? <ErrorCircle20Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                                        : <Warning20Filled    style={{ color: tokens.colorPaletteMarigoldForeground1 }} />}
                                    </span>
                                    <div>
                                      <span className={styles.issueRecord}>{iss.record}</span>
                                      <span style={{ color: tokens.colorNeutralForeground3, margin: "0 4px" }}>·</span>
                                      <span className={styles.issueField}>{iss.field}</span>
                                      <br />
                                      <span className={styles.issueText}>{iss.issue}</span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}

                      <p style={{ fontSize: "11px", color: tokens.colorNeutralForeground3, marginTop: "8px" }}>
                        Generated: {report.generatedAt ? new Date(report.generatedAt).toLocaleString() : "—"}
                        &nbsp;·&nbsp; Scopes: {(report.scopes || []).join(", ")}
                      </p>
                    </>
                  )}
                </DialogContent>
                <DialogActions style={{ justifyContent: "flex-end" }}>
                  <Button appearance="secondary" onClick={() => setReportOpen(false)}>Close</Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          {/* Data scopes */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Data Scopes</div>
            <div className={styles.scopeGroup}>
              <Checkbox
                label="Client - Info"
                checked={scopes["client-info"]}
                onChange={(_, d) => setScopes({ ...scopes, "client-info": !!d.checked })}
              />
              <Checkbox
                label="Client - Finances"
                checked={scopes["client-finances"]}
                onChange={(_, d) => setScopes({ ...scopes, "client-finances": !!d.checked })}
              />
            </div>
            <p style={{ fontSize: "12px", color: tokens.colorNeutralForeground3, marginTop: "8px" }}>
              Transfer method: <strong>Overwrite</strong> — existing CashCalc data will be replaced.
            </p>
          </div>

          {/* Sync results — colour-coded, no partner-info, with View Details */}
          {displayedResults.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Sync Results</div>
              {displayedResults.map((r: any, i: number) => {
                const c = statusColors(r.status);
                return (
                  <div
                    key={i}
                    className={styles.resultRow}
                    style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, color: c.text }}
                  >
                    <SectionStatusIcon status={r.status} />
                    <span className={styles.resultLabel}>
                      <strong>{SCOPE_LABELS[r.scope] ?? r.scope}</strong>
                      <span style={{ fontWeight: 400, marginLeft: "8px" }}>— {statusLabel(r.status)}</span>
                    </span>
                    <Button
                      appearance="transparent"
                      size="small"
                      style={{ color: c.text, textDecoration: "underline", padding: "0 4px" }}
                      onClick={() => { setDetailScope(r); setDetailOpen(true); }}
                    >
                      View Details
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Export + Validate action buttons (left) / Reset (right) */}
          <div className={styles.syncActions}>
            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                appearance="primary"
                className="asc-button-primary"
                shape="rounded"
                size="medium"
                icon={syncLoading ? <Spinner size="tiny" /> : undefined}
                onClick={handleSync}
                disabled={syncLoading || !hasMinimumSyncRequirements}
              >
                {syncLoading ? "Exporting…" : "Export to CashCalc"}
              </Button>
              <div style={{ position: "relative" }}>
                <Button
                  className={styles.invertedBrandBtn}
                  shape="rounded"
                  size="medium"
                  icon={validateLoading ? <Spinner size="tiny" /> : undefined}
                  onClick={handleValidateClick}
                  disabled={validateLoading || !hasMinimumSyncRequirements}
                >
                  {validateLoading ? "Validating…" : "Validate Data"}
                </Button>
                <Popover withArrow openOnHover mouseLeaveDelay={0} positioning="above-start">
                  <PopoverTrigger disableButtonEnhancement>
                    <Button
                      icon={<Info12Regular style={{ fontSize: "14px" }} />}
                      shape="circular"
                      size="small"
                      aria-label="What does Validate Data do?"
                      style={{
                        position: "absolute", top: 0, left: "calc(100% + 6px)",
                        minWidth: 0, width: "16px", height: "16px", padding: 0,
                        border: "none", background: "transparent",
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverSurface style={{ maxWidth: "260px" }}>
                    <div style={{ fontSize: "10px" }}>
                      <strong>What does Validate Data do?</strong>
                      <p style={{ marginTop: "6px", marginBottom: 0, textAlign: "justify" }}>
                        Preview issues before you export. It checks the selected client's data against the scopes you've chosen and flags anything missing or incorrect — nothing is sent to CashCalc, so it's safe to run anytime.
                      </p>
                    </div>
                  </PopoverSurface>
                </Popover>
              </div>
            </div>
            <Button
              appearance="secondary"
              className="asc-button-secondary"
              style={{ color: "black" }}
              onClick={handleResetSelection}
            >
              New Export
            </Button>
          </div>

          {/* ── Sync progress dialog ── */}
          <Dialog open={progressOpen} onOpenChange={(_, d) => { if (!d.open) handleProgressClose(); }}>
            <DialogSurface style={{ maxWidth: "400px", width: "90vw" }}>
              <DialogBody>
                {progressDone && (
                  <DialogTitle>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <CheckmarkCircle20Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                      <span>Sync Complete</span>
                    </div>
                  </DialogTitle>
                )}
                <DialogContent style={{ maxHeight: "60vh", overflowY: "auto" }}>
                  {!progressDone ? (
                    /* Waiting for server response — single centered spinner, no separate buffer text */
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                      <Spinner size="large" label="Syncing to CashCalc…" />
                    </div>
                  ) : (
                    /* Server responded — reveal scopes and sections sequentially */
                    progressScopes.map((scopeKey) => {
                      const scopeSections = SCOPE_SECTIONS[scopeKey] || [];
                      const anySectionVisible = scopeSections.some(
                        (sec) => `${scopeKey}:${sec.key}` in revealedData
                      );
                      if (!anySectionVisible) return null;

                      const allScopeDone = scopeSections.every((sec) => {
                        const d = revealedData[`${scopeKey}:${sec.key}`];
                        return d !== undefined && d !== null;
                      });

                      return (
                        <div key={scopeKey}>
                          <div
                            className={styles.progressScopeHeading}
                            style={{ display: "flex", alignItems: "center", gap: "8px" }}
                          >
                            {allScopeDone
                              ? <CheckmarkCircle20Filled style={{ color: tokens.colorPaletteGreenForeground1, flexShrink: 0 }} />
                              : <Spinner size="tiny" style={{ flexShrink: 0 }} />}
                            {SCOPE_LABELS[scopeKey] ?? scopeKey}
                          </div>
                          {scopeSections.map((sec) => {
                            const rvKey = `${scopeKey}:${sec.key}`;
                            if (!(rvKey in revealedData)) return null; // not yet appeared

                            const secData  = revealedData[rvKey]; // null = loading, obj = done
                            const secStatus: string | undefined = secData?.status;
                            const c = secStatus ? statusColors(secStatus) : undefined;
                            return (
                              <div key={sec.key} className={styles.progressRow}>
                                <SectionStatusIcon status={secStatus} />
                                <span className={styles.progressRowLabel}>{sec.label}</span>
                                {secStatus && (
                                  <span className={styles.progressStatus} style={{ color: c?.text }}>
                                    {statusLabel(secStatus)}
                                    {secData?.synced != null && ` (${secData.synced})`}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                  {progressDone && (
                    <p style={{ fontSize: "12px", color: tokens.colorNeutralForeground3, marginTop: "12px", textAlign: "center" }}>
                      Closing automatically…
                    </p>
                  )}
                </DialogContent>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          {/* ── View Details dialog ── */}
          <Dialog open={detailOpen} onOpenChange={(_, d) => setDetailOpen(d.open)}>
            <DialogSurface style={{ maxWidth: "600px", width: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
              <DialogBody style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <DialogTitle>
                  Sync Detail : {detailScope && (SCOPE_LABELS[detailScope.scope] ?? detailScope.scope)} — {selectedWaClientName}
                </DialogTitle>
                <DialogContent style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                  {detailScope && (SCOPE_SECTIONS[detailScope.scope] || []).map((sec) => {
                    const secData = detailScope.data?.[sec.key];
                    if (!secData) {
                      return (
                        <div key={sec.key} style={{ padding: "10px 0", borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: "13px" }}>{sec.label}</span>
                            <Badge appearance="outline" size="small">Not in scope</Badge>
                          </div>
                        </div>
                      );
                    }

                    const c        = statusColors(secData.status);
                    const hasErrors = secData.failed > 0 || (secData.errors?.length > 0) || secData.notesFailed > 0;

                    // "Overwritten" replaces the raw synced/deleted pair — clearer for users than
                    // two separate numbers. deleted === 0 means nothing existed yet (first sync),
                    // so nothing was actually overwritten. A mismatch between the two counts means
                    // either a duplicate was left behind on CashCalc (synced > deleted) or a record
                    // failed to recreate after the old one was removed (deleted > synced).
                    const syncedCount = secData.synced ?? 0;
                    const deletedCount = secData.deleted ?? 0;
                    const overwritten = Math.min(syncedCount, deletedCount);
                    const duplicated  = syncedCount > deletedCount ? syncedCount - deletedCount : 0;
                    const missing     = deletedCount > syncedCount ? deletedCount - syncedCount : 0;

                    return (
                      <div key={sec.key} style={{ padding: "10px 0", borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                        {/* Section header */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: hasErrors ? "8px" : 0 }}>
                          <SectionStatusIcon status={secData.status} />
                          <span style={{ flex: 1, fontWeight: 600, fontSize: "13px" }}>{sec.label}</span>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: c.text }}>{statusLabel(secData.status)}</span>
                        </div>

                        {/* Counts row */}
                        <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: tokens.colorNeutralForeground2, paddingLeft: "28px", marginBottom: hasErrors ? "6px" : 0 }}>
                          {deletedCount > 0 ? (
                            <>
                              {overwritten > 0 && (
                                <span style={{ color: tokens.colorPaletteGreenForeground1 }}>↻ {overwritten} overwritten</span>
                              )}
                              {duplicated > 0 && (
                                <span style={{ color: tokens.colorPaletteMarigoldForeground1 }}>⚠ {duplicated} duplicated</span>
                              )}
                              {missing > 0 && (
                                <span style={{ color: tokens.colorPaletteMarigoldForeground1 }}>⚠ {missing} missing</span>
                              )}
                            </>
                          ) : (
                            secData.synced != null && (
                              <span style={{ color: tokens.colorPaletteGreenForeground1 }}>✓ {secData.synced} synced</span>
                            )
                          )}
                          {secData.failed > 0 && (
                            <span style={{ color: tokens.colorPaletteRedForeground1 }}>✕ {secData.failed} failed</span>
                          )}
                          {secData.deleteFailed > 0 && (
                            <span style={{ color: tokens.colorPaletteMarigoldForeground1 }}>⚠ {secData.deleteFailed} delete failed</span>
                          )}
                          {secData.notesSent > 0 && (
                            <span style={{ color: tokens.colorPaletteGreenForeground1 }}>✓ {secData.notesSent} note{secData.notesSent !== 1 ? "s" : ""} sent</span>
                          )}
                          {secData.notesFailed > 0 && (
                            <span style={{ color: tokens.colorPaletteRedForeground1 }}>✕ {secData.notesFailed} note{secData.notesFailed !== 1 ? "s" : ""} failed</span>
                          )}
                          {secData.status === "delete_skipped" && (
                            <span style={{ color: tokens.colorNeutralForeground3 }}>Skipped — existing records could not be fetched; re-run to retry.</span>
                          )}
                        </div>

                        {/* Error list */}
                        {secData.errors?.length > 0 && (
                          <ul style={{ margin: "4px 0 0 28px", padding: 0, listStyle: "disc", fontSize: "12px", color: tokens.colorPaletteRedForeground1 }}>
                            {secData.errors.map((err: string, ei: number) => (
                              <li key={ei} style={{ marginBottom: "2px" }}>{err}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </DialogContent>
                <DialogActions style={{ justifyContent: "flex-end" }}>
                  <Button appearance="secondary" onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </>
      )}

      {/* WA client picker — reuses DynamicReport's PopupTable */}
      <PopupTable
        param={CLIENT_PARAM}
        popupState={clientPopup}
        onClose={handleClientPopupClose}
        onSelect={handleClientSelect}
        onRowSelectionChange={handleClientRowSelection}
        onPaginationChange={handleClientPagination}
        onGlobalFilterChange={handleClientGlobalFilter}
      />
    </div>
  );
};

export default CashCalcPage;
